import { faPlusSquare, faUpload, faDownload, faSpinner, faCircleXmark, faPencil } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect, useRef } from 'react'
import { ImageConfig } from './ImageConfig'

import useAxiosPrivateSAI from '../../../../hooks/useAxiosPrivateSAI'
import useAuth from '../../../../hooks/useAuth'
import moment from 'moment'

const ViewClaim = ({ claim, policy }) => {
  const { auth, createInfo } = useAuth();

  const axios = useAxiosPrivateSAI()
  
  const [phasedReturn, setPhasedReturn] = useState(claim?.claim_phased || false);
  const [showPolicyDetails, setShowPolicyDetails] = useState(false)
  const [uploadingSections, setUploadingSections] = useState({})
  const [evidenceSections, setEvidenceSections] = useState([])
  const [commAttachments, setCommAttachments] = useState([])
  const [savingPhased, setSavingPhased] = useState(false)
  const [editingField, setEditingField] = useState({});
  const [selectedFile, setSelectedFile] = useState()
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMsg, setErrorMsg] = useState(false)
  const [showFiles, setShowFiles] = useState([])
  const [fileList, setFileList] = useState([])

  const wrapperRef = useRef(null)

  const allowedFileTypes = [
    '.doc',
    '.docx',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls',
    '.xlsx',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    '.csv',
    'application/pdf',
    '.pdf',
    'image/jpeg',
    '.jpg',
    'image/png',
    '.png',
    'image/tiff',
    '.tiff',
  ];

  useEffect(() => {
    const fetchEvidence = async () => {
      if (!claim?.claim_id) return;

      const { data } = await axios.get(`/api/v1/staff-hub/claims/${claim.claim_id}/evidence?api_token=${process.env.REACT_APP_KIS_TOKEN}`);
      const transformed = Object.values(data).map((item) => {
        let extrasArr = []
        try {
          extrasArr = JSON.parse(item.extra_content || '[]')
        } catch {
          extrasArr = []
        }

        const getExtra = (label) =>
          extrasArr.find((x) => x.label.toLowerCase() === label.toLowerCase())?.content || ''

        return {
          ...item,
          visible: false,
          editMode: false,
          startDate: item.valid_start || '',
          endDate: item.valid_until || '',
          condition: getExtra('Condition'),
          doctorName: getExtra('doctor_name'),
          doctorPhone: getExtra('doctor_number'),
          doctorAddress: getExtra('doctor_address'),
        }
      })

      setEvidenceSections(transformed);
    };

    fetchEvidence();
  }, [claim]);

  useEffect(() => {
    setPhasedReturn(claim?.claim_info?.phased ? true : false)
  }, [claim])

  const handleTogglePhasedReturn = async () => {
    const newState = !phasedReturn
    setPhasedReturn(newState)
    setSavingPhased(true)

    try {
      await axios.put(`/api/claims/phased/${claim.claim_id}`, {
        updated_by: auth.user.id,
        claim_phased: newState,
      })

    } catch {
      createInfo('error', 'Failed to update Phased Return.')
    }

    setSavingPhased(false)
  }

  const toggleSectionVisibility = (id) => {
    setEvidenceSections((prev) =>
      prev.map((section) =>
        section.evidence_id === id ? { ...section, visible: !section.visible, editMode: false } : section
      )
    )
  }

  const editSectionData = (id, doc) => {
    setEvidenceSections((prev) =>
      prev.map((section) =>
        section.evidence_id === id
          ? {
            ...section,
            editMode: true,
            visible: false,
            startDate: doc.valid_start ? moment(doc.valid_start).format("YYYY-MM-DD") : "",
            endDate: doc.valid_until ? moment(doc.valid_until).format("YYYY-MM-DD") : "",
            condition: (() => {
              const parsed = JSON.parse(doc.extra_content || "[]");
              return parsed.find((f) => f.label === "Condition")?.content || ""
            }
            )(),
            doctorName: (() => {
              try {
                const parsed = JSON.parse(doc.extra_content || "[]");
                return parsed.find((f) => f.label === "doctor_name")?.content || ""
              } catch {
                return ""
              }
            })(),
            doctorPhone: (() => {
              try {
                const parsed = JSON.parse(doc.extra_content || "[]");
                return parsed.find((f) => f.label === "doctor_number")?.content || ""
              } catch {
                return ""
              }
            })(),
            doctorAddress: (() => {
              try {
                const parsed = JSON.parse(doc.extra_content || "[]")
                return parsed.find((f) => f.label === "doctor_address")?.content || ""
              } catch {
                return ""
              }
            })(),
          }
          : section
      )
    )
  }

  const updateSectionDate = (id, field, value) => {
    setEvidenceSections((prev) =>
      prev.map((section) =>
        section.evidence_id === id ? { ...section, [field]: value } : section
      )
    )
  }

  const handleUpload = async (section) => {
    setUploadingSections(prev => ({ ...prev, [section.evidence_id]: true }))

    const file = fileList[0]
    if (!file) {
      createInfo('error', 'Please select a file first.')
      return
    }

    const formData = new FormData()
    formData.append("file", file)
    formData.append("claim_id", claim.claim_id);
    formData.append("evidence_id", section.evidence_id);
    formData.append("valid_start", section.startDate);
    formData.append("valid_until", section.endDate);

    const extraContent =
      section.evidence_title === "Medical Consent"
        ? [
          { label: "Doctor's Name", content: section.doctorName || "" },
          { label: "Doctor's Phone", content: section.doctorPhone || "" },
          { label: "Doctor's Address", content: section.doctorAddress || "" },
        ]
        : [
          {
            label: "Condition",
            content: section.condition || "",
            placeholder: "Condition",
            type: "textarea",
            required: "false",
          },
        ]

    formData.append("extra_content", JSON.stringify(extraContent))

    try {
      const uploadRes = await axios.post(
        `/api/v1/upload/document?area=policy/claims/${claim.claim_id}&type=claim&cid=${claim.claim_id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (uploadRes?.data?.data?.resource_id) {
        const evidencePayload = {
          claim_id: claim.claim_id,
          evidence_id: section.evidence_id,
          valid_start: section.startDate,
          valid_until: section.endDate,
          extra_content: JSON.stringify(extraContent),
          resource_id: uploadRes.data.data.resource_id,
        };

        await axios.post(`/api/v1/claims/evidence?api_token=${process.env.REACT_APP_KIS_TOKEN}`, evidencePayload);

        const startMatch = claim.claim_absence_start_date === section.startDate;
        const endMatch = claim.claim_absence_end_date === section.endDate;

        if (
          (section.evidence_id === '00499e10-33cc-11e7-80f2-cbd5f9bacaa9' && startMatch) ||
          (section.evidence_id === '00499e10-33cc-11e7-80f2-cbd5f9bacaa8' && startMatch && endMatch)
        ) {
          setTimeout(() => window.location.reload(), 5000)
        } else {
          createInfo('success', 'Document uploaded successfully')
        }
      } else {
        createInfo('error', 'File uploaded but no resource ID returned.')
      }
    } catch (error) {
      createInfo('error', error?.response?.data?.message || error.message || 'Upload failed')
    }
    setTimeout(() => window.location.reload(), 5000)
    setUploadingSections(prev => ({ ...prev, [section.evidence_id]: false }))
  }

  const onDragEnter = () => wrapperRef.current.classList.add('dragover')
  const onDragLeave = () => wrapperRef.current.classList.remove('dragover')

  const onDragOver = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }
  const onDrop = (e) => {
    e.preventDefault()
    wrapperRef.current.classList.remove('dragover')
    onFileDrop(e.nativeEvent.dataTransfer.files)
  }

  const isValidFileType = (file) => {
    const extension = `.${file.name.split('.').pop()}`
    const mimeType = file.type
    return (
      allowedFileTypes.includes(extension) ||
      allowedFileTypes.includes(mimeType)
    )
  }
  const onFileUpload = async (e) => {
    e.preventDefault()
    const newFile = e.target.files[0]

    if (newFile && isValidFileType(newFile)) {
      const updatedList = [...fileList, newFile]
      setFileList(updatedList)
    } else {
      createInfo('error', 'Invalid file type. Please select a valid file.')
    }
  }

  const onFileDrop = async (files) => {
    const newFile = files[0]

    if (newFile && isValidFileType(newFile)) {
      const updatedList = [...fileList, newFile]
      setFileList(updatedList)
    } else {
      createInfo('error', 'Invalid file type')
      return
    }
  }

  const fileRemove = (file) => {
    const updatedList = [...fileList]
    updatedList.splice(fileList.indexOf(file), 1)
    setFileList(updatedList)
  }

  const saveField = async (doc, field, value) => {
    try {
      if (field === "valid_start" || field === "valid_until") {
        const res = await axios.post(`/api/v1/claims/evidence_document/${doc.evidence_document_id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
          column: field,
          [field]: value,
        });

        if (!res.data.error) {
          Object.assign(doc, res.data.data);
          createInfo('success', `${field.replace('_', ' ')} updated`)
        }
      } else {
        let extraFields = []
        try {
          extraFields = JSON.parse(doc.extra_content || "[]")
        } catch {
          extraFields = []
        }

        const updatedFields = extraFields.map((f) =>
          f.label.toLowerCase() === field.toLowerCase()
            ? { ...f, content: value }
            : f
        )

        await axios.post(
          `/api/v1/claims/evidence_document/${doc.evidence_document_id}/extra_content?api_token=${process.env.REACT_APP_KIS_TOKEN}`,
          {
            extra_content: JSON.stringify(updatedFields),
          }
        )

        createInfo('success', `${field.replace('_', ' ')} updated`)
        doc.extra_content = JSON.stringify(updatedFields)
      }

      setEditingField((prev) => ({ ...prev, [doc.evidence_document_id]: null }))
    } catch (error) {
      console.error("Failed to save field", error)
      createInfo('error', 'Failed to update field')
    }
  }

  return (
    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
      {claim?.can_expire && claim?.expiry_date && claim?.claim_documents_requested && (
        <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-md text-sm text-center">
          <p className="text-base font-semibold">
            This claim will be <br /> closed in {moment(claim.expiry_date).diff(moment(), 'days')} days
          </p>
          <p>
            If requested documents are not received the claim will be closed on{' '}
            {moment(claim.expiry_date).format('Do MMMM YYYY')}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last date of absence</label>
          <input
            type="date"
            defaultValue={claim?.end_date || ''}
            placeholder="Enter last date"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            disabled={!!claim?.end_date}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phased return</label>
          <div
            className={`relative inline-flex w-11 shrink-0 rounded-full p-0.5 transition-colors duration-200 ease-in-out 
                          ${phasedReturn ? 'bg-indigo-600' : 'bg-gray-200'}
                          ${phasedReturn ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}
                        `}
          >
            <input
              id="phased-return-toggle"
              name="phased-return"
              type="checkbox"
              disabled={phasedReturn}
              className="peer absolute inset-0 z-10 opacity-0 cursor-pointer disabled:cursor-not-allowed"
              checked={phasedReturn}
              onChange={(e) => {
                if (!phasedReturn) {
                  handleTogglePhasedReturn(e)
                }
              }}
              aria-label="Use setting"
            />
            <span className="relative size-5 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out peer-checked:translate-x-5">
              {phasedReturn ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <svg fill="currentColor" viewBox="0 0 12 12" className="size-3 text-indigo-600">
                    <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                  </svg>
                </span>
              ) : (
                <span className="absolute inset-0 flex items-center justify-center">
                  <svg fill="none" viewBox="0 0 12 12" className="size-3 text-gray-400">
                    <path
                      d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {evidenceSections.map((section) => (
        <div key={section.evidence_id}>
          <div className='flex items-center space-x-2 mb-4'>
            <h3 className="text-base font-semibold">{section.evidence_title}</h3>
            <div>
              <button
                onClick={() => toggleSectionVisibility(section.evidence_id)}
                className="inline-flex items-center border border-sas text-sas rounded-md overflow-hidden text-xs font-medium"
              >
                <span className="px-4 py-1">Add Evidence</span>
                <span className="bg-sas text-white py-2 px-3 flex items-center justify-center">
                  <FontAwesomeIcon icon={faPlusSquare} />
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {section.documents && section.documents.length > 0 ? (
              section.documents.map((doc, index) => {
                let extraFields = [];
                try {
                  extraFields = JSON.parse(doc.extra_content || "[]");
                } catch {
                  extraFields = [];
                }

                const currentEditing = editingField[doc.evidence_document_id];
                const getExtra = (label) =>
                  extraFields.find((f) => f.label.toLowerCase() === label.toLowerCase())?.content || "";

                return (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 text-sm"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-gray-800">
                        {doc.resources?.resource_name || section.evidence_title}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded capitalize ${doc.evidence_document_status === "accepted"
                          ? "bg-green-100 text-green-700"
                          : doc.evidence_document_status === "pending"
                            ? "bg-orange-100 text-orange-700"
                            : doc.evidence_document_status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : doc.evidence_document_status === "expired"
                                ? "bg-purple-100 text-purple-700"
                                : doc.evidence_document_status === "required"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-100 text-gray-600"
                          }`}
                      >
                        {doc.evidence_document_status}
                      </span>
                    </div>

                    <p className="text-xs text-gray-500">
                      Submitted: {moment(doc.created_at).format("DD/MM/YYYY")}
                    </p>

                    {doc.evidence_document_status === "required" ? (
                      <p className="text-xs font-medium text-red-600 mt-2">
                        Please upload a {section.evidence_title}
                      </p>
                    ) : section.evidence_title === "Medical Consent" ? (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center justify-between">
                          {currentEditing === "doctor_name" ? (
                            <input
                              type="text"
                              className="w-full border rounded px-2 py-1 text-xs"
                              defaultValue={getExtra("doctor_name")}
                              onBlur={(e) => {
                                saveField(doc, "doctor_name", e.target.value);
                                setEditingField((prev) => ({ ...prev, [doc.evidence_document_id]: null }));
                              }}
                              autoFocus
                            />
                          ) : (
                            <p className="text-xs text-gray-700">
                              <strong>Doctor's Name:</strong> {getExtra("doctor_name")}
                              <FontAwesomeIcon
                                icon={faPencil}
                                className="ml-2 text-gray-500 cursor-pointer hover:text-sas"
                                onClick={() =>
                                  setEditingField((prev) => ({
                                    ...prev,
                                    [doc.evidence_document_id]: "doctor_name",
                                  }))
                                }
                              />
                            </p>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          {currentEditing === "doctor_number" ? (
                            <input
                              type="text"
                              className="w-full border rounded px-2 py-1 text-xs"
                              defaultValue={getExtra("doctor_number")}
                              onBlur={(e) => {
                                saveField(doc, "doctor_number", e.target.value);
                                setEditingField((prev) => ({ ...prev, [doc.evidence_document_id]: null }));
                              }}
                              autoFocus
                            />
                          ) : (
                            <p className="text-xs text-gray-700">
                              <strong>Doctor's Phone:</strong> {getExtra("doctor_number")}
                              <FontAwesomeIcon
                                icon={faPencil}
                                className="ml-2 text-gray-500 cursor-pointer hover:text-sas"
                                onClick={() =>
                                  setEditingField((prev) => ({
                                    ...prev,
                                    [doc.evidence_document_id]: "doctor_number",
                                  }))
                                }
                              />
                            </p>
                          )}
                        </div>

                        <div className="flex items-start justify-between">
                          {currentEditing === "doctor_address" ? (
                            <textarea
                              className="w-full border rounded px-2 py-1 text-xs"
                              defaultValue={getExtra("doctor_address")}
                              onBlur={(e) => {
                                saveField(doc, "doctor_address", e.target.value);
                                setEditingField((prev) => ({ ...prev, [doc.evidence_document_id]: null }));
                              }}
                              autoFocus
                            />
                          ) : (
                            <p className="text-xs text-gray-700">
                              <strong>Doctor's Address:</strong> {getExtra("doctor_address")}
                              <FontAwesomeIcon
                                icon={faPencil}
                                className="ml-2 text-gray-500 cursor-pointer hover:text-sas"
                                onClick={() =>
                                  setEditingField((prev) => ({
                                    ...prev,
                                    [doc.evidence_document_id]: "doctor_address",
                                  }))
                                }
                              />
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 space-y-2">
                        <div className='flex items-center space-x-4'>
                          <div className="flex items-center justify-between">
                            {currentEditing === "valid_start" ? (
                              <input
                                type="date"
                                className="w-full border rounded px-2 py-1 text-xs"
                                defaultValue={doc.valid_start ? moment(doc.valid_start).format("YYYY-MM-DD") : ""}
                                onBlur={(e) => {
                                  saveField(doc, "valid_start", e.target.value);
                                  setEditingField((prev) => ({ ...prev, [doc.evidence_document_id]: null }));
                                }}
                                autoFocus
                              />
                            ) : (
                              <p className="text-xs text-gray-700">
                                <strong>Valid From:</strong>{" "}
                                {doc.valid_start ? moment(doc.valid_start).format("DD/MM/YYYY") : "—"}
                                <FontAwesomeIcon
                                  icon={faPencil}
                                  className="ml-2 text-gray-500 cursor-pointer hover:text-sas"
                                  onClick={() =>
                                    setEditingField((prev) => ({
                                      ...prev,
                                      [doc.evidence_document_id]: "valid_start",
                                    }))
                                  }
                                />
                              </p>
                            )}
                          </div>

                          <div className="flex items-center justify-between">
                            {currentEditing === "valid_until" ? (
                              <input
                                type="date"
                                className="w-full border rounded px-2 py-1 text-xs"
                                defaultValue={doc.valid_until ? moment(doc.valid_until).format("YYYY-MM-DD") : ""}
                                onBlur={(e) => {
                                  saveField(doc, "valid_until", e.target.value);
                                  setEditingField((prev) => ({ ...prev, [doc.evidence_document_id]: null }));
                                }}
                                autoFocus
                              />
                            ) : (
                              <p className="text-xs text-gray-700">
                                <strong>Valid Until:</strong>{" "}
                                {doc.valid_until ? moment(doc.valid_until).format("DD/MM/YYYY") : "—"}
                                <FontAwesomeIcon
                                  icon={faPencil}
                                  className="ml-2 text-gray-500 cursor-pointer hover:text-sas"
                                  onClick={() =>
                                    setEditingField((prev) => ({
                                      ...prev,
                                      [doc.evidence_document_id]: "valid_until",
                                    }))
                                  }
                                />
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-start justify-between">
                          {currentEditing === "condition" ? (
                            <textarea
                              className="w-full border rounded px-2 py-1 text-xs"
                              defaultValue={getExtra("Condition")}
                              onBlur={(e) => {
                                saveField(doc, "condition", e.target.value);
                                setEditingField((prev) => ({ ...prev, [doc.evidence_document_id]: null }));
                              }}
                              autoFocus
                            />
                          ) : (
                            <p className="text-xs text-gray-700">
                              <strong>Condition:</strong> {getExtra("Condition")}
                              <FontAwesomeIcon
                                icon={faPencil}
                                className="ml-2 text-gray-500 cursor-pointer hover:text-sas"
                                onClick={() =>
                                  setEditingField((prev) => ({
                                    ...prev,
                                    [doc.evidence_document_id]: "condition",
                                  }))
                                }
                              />
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500">No documents uploaded</p>
            )}
          </div>

          {(section.visible || section.editMode) && (
            <div className="mt-4 space-y-4">
              {section.evidence_title === "Medical Consent" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Doctor's Name</label>
                    <input
                      type="text"
                      value={section.doctorName || ""}
                      onChange={(e) =>
                        updateSectionDate(section.evidence_id, "doctorName", e.target.value)
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Doctor's Phone Number</label>
                    <input
                      type="text"
                      value={section.doctorPhone || ""}
                      onChange={(e) =>
                        updateSectionDate(section.evidence_id, "doctorPhone", e.target.value)
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Doctor's Address</label>
                    <textarea
                      value={section.doctorAddress || ""}
                      onChange={(e) =>
                        updateSectionDate(section.evidence_id, "doctorAddress", e.target.value)
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={section.startDate || ''}
                      onChange={(e) => updateSectionDate(section.evidence_id, 'startDate', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={section.endDate || ''}
                      onChange={(e) => updateSectionDate(section.evidence_id, 'endDate', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                    <textarea
                      value={section.condition || ''}
                      onChange={(e) => updateSectionDate(section.evidence_id, 'condition', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}
              {(section.evidence_title === "Medical Consent"
                ? section.doctorName && section.doctorPhone
                : section.startDate && section.endDate) && (
                  <>
                    <div className='border border-dashed border-gray-300 rounded-md'>
                      <label
                        htmlFor={`file-upload-${section.evidence_id}`}
                        ref={wrapperRef}
                        onDragEnter={onDragEnter}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        className=" p-6 text-center relative"
                      >
                        <input
                          type="file"
                          id={`file-upload-${section.evidence_id}`}
                          accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          onChange={onFileUpload}
                        />
                        <div className="mb-3 pointer-events-none">
                          <img src="/images/laptop.png" alt="Upload" className="mx-auto h-20" />
                        </div>
                        <p className="text-sas font-semibold text-sm pointer-events-none">Drag and Drop or click to upload a file</p>
                        <p className="text-xs text-gray-500 pointer-events-none">PNG, JPG, XLS or PDF up to 10MB</p>
                      </label>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mt-8">
                      {fileList.map((item, index) => (
                        <div key={index} className="drop-file-preview__item flex items-center justify-between">
                          <div className="flex items-center">
                            <img
                              src={ImageConfig[item.type.split('/')[1]] || ImageConfig['default']}
                              alt=""
                              className="w-16"
                            />
                            <div className="drop-file-preview__item__info">
                              <p className="break-words">{item.name}</p>
                              <p>{item.size} KB</p>
                            </div>
                          </div>
                          <span
                            className="hover:text-red-700 drop-file-preview__item__del"
                            onClick={() => fileRemove(item)}
                          >
                            <FontAwesomeIcon icon={faCircleXmark} className="text-gray-700 hover:text-red-700 w-5 h-5" />
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleUpload(section)}
                        disabled={fileList.length === 0 || uploadingSections[section.evidence_id]}
                        className={`inline-flex items-center border border-sas rounded-md overflow-hidden text-sm font-medium ${fileList.length === 0 || uploadingSections[section.evidence_id] ? 'opacity-50 cursor-not-allowed' : 'text-sas'
                          }`}
                      >
                        <span className="px-4 py-1"> {uploadingSections[section.evidence_id] ? 'Uploading...' : 'Upload'}</span>
                        <span className="bg-sas text-white py-2 px-3 flex items-center justify-center">
                          {uploadingSections[section.evidence_id] ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faUpload} />}
                        </span>
                      </button>

                      {section.evidence_template_alt && (
                        <a
                          href={section.evidence_template_alt}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center border border-sas text-sas rounded-md overflow-hidden text-sm font-medium hover:shadow-md"
                        >
                          <span className="px-4 py-1">Download</span>
                          <span className="bg-sas text-white py-2 px-3 flex items-center justify-center">
                            <FontAwesomeIcon icon={faDownload} />
                          </span>
                        </a>
                      )}
                    </div>
                  </>
                )}
            </div>
          )}
        </div>
      ))}

      <div className="mt-8">
        <button
          onClick={() => setShowPolicyDetails(prev => !prev)}
          className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 rounded-md text-left text-sm font-semibold"
        >
          Policy Details
          <span className="text-lg">{showPolicyDetails ? '▴' : '▾'}</span>
        </button>

        {showPolicyDetails && policy?.policy_information && (
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full text-[10px] text-left text-gray-800 overflow-hidden rounded-lg">
              <thead >
                <tr className="bg-sas text-white text-xs text-start">
                  <th className=" px-4 py-2">Staff</th>
                  <th className=" px-4 py-2">FTE</th>
                  <th className=" px-4 py-2">Daily Reimbursement</th>
                  <th className=" px-4 py-2">Waiting period</th>
                  <th className=" px-4 py-2">Benefit Days</th>
                  <th className=" px-4 py-2">Stress Limit</th>
                  <th className=" px-4 py-2">Maternity Reimbursement</th>
                </tr>
              </thead>
              <tbody className="text-center text-gray-700 text-sm">
                {(() => {
                  const info = JSON.parse(policy.policy_information);
                  const items = info.items || [];

                  return items.map((item, idx) => (
                    <tr key={idx} className={idx === items.length - 1 ? "bg-white even:bg-gray-50 even:rounded-b-lg" : "bg-white even:bg-gray-50"}>
                      <td className="px-4 py-2 rounded-b-lg">{item.staffType}</td>
                      <td className="px-4 py-2">{parseFloat(item.fte).toFixed(2)}</td>
                      <td className="px-4 py-2">{item.benefit}</td>
                      <td className="px-4 py-2">{item.excess}</td>
                      <td className="px-4 py-2">{item.period}</td>
                      <td className="px-4 py-2">{item.stress}</td>
                      <td className="px-4 py-2">
                        {item.maternityCover ? item.maternityCover : 'Excluded'}
                      </td>
                    </tr>
                  ))
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default ViewClaim
